import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import roles from "src/data/roles";
import i18next from "src/i18n/config";
import { withRequiredRole } from "src/ui/navigation/utils/withRequiredRole";
import { PrimaryButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import {
    MinimalTable,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "src/ui/shared/table/MinimalTableComponents";
import { Header } from "src/ui/shared/table/TableHeader";

import { FetchAllFdroidReposPayload } from "@admin/api/fdroid";
import { useGetAllFdroidRepos } from "@admin/hooks/useGetAllFdroidRepos";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Card, CardContent, CardHeader, TableRow, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { TableBody } from "@shared/table/TableBody";
import CustomTablePagination from "@shared/table/TablePagination";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import { useStyles } from "./AdminFdroidPage.style";
import { FormValues, isFilterApplied, tableColumns } from "./AdminFdroidPage.utils";
import TableItem from "./TableItem";

const AdminFdroidPage = () => {
    const [filterState, setFilterState] = React.useState<FormValues>({});
    const [sorting, setSorting] = React.useState<SortingState>([{ id: "name", desc: false }]);
    const [pageIndex, setPageIndex] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(12);
    const { classes } = useStyles();
    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const { t } = useTranslation(["pages", "common"]);

    const currentSort = sorting[0] ?? { id: "name", desc: false };

    const filterActive = isFilterApplied(filterState);

    const payload: FetchAllFdroidReposPayload = {
        pageIndex: pageIndex + pageIndexOffset,
        pageSize,
        sortCriteria: currentSort.id,
        sortDirection: currentSort.desc ? 1 : 0,
        workspaceId: filterState.workspaceId ? +filterState.workspaceId : undefined,
        name: filterState.name,
        title: filterState.title,
        url: filterState.url,
    };

    const { data, isLoading, isError, refetch } = useGetAllFdroidRepos(payload);

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [pageIndex, pageSize, data.list?.length]);

    const table = useReactTable<API.FdroidRepoAdmin>({
        data: data.list ?? [],
        columns: tableColumns,
        manualPagination: true,
        manualSorting: true,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const onSubmit = (values: FormValues) => {
        setPageIndex(0);
        setFilterState(values);
    };

    const onClear = (form: FormApi<FormValues, Partial<FormValues>>) => {
        form.reset();
        setFilterState({});
        setPageIndex(0);
    };

    const handleResetFilter = () => {
        setFilterState({});
        setPageIndex(0);
    };

    return (
        <PageContainer fitViewport>
            <Form<FormValues>
                onSubmit={onSubmit}
                render={({ handleSubmit, form, pristine }) => (
                    <form onSubmit={handleSubmit}>
                        <Card className={classes.card}>
                            <CardHeader
                                title={
                                    <Box className={classes.cardTitleBox}>
                                        <Typography variant="h6">{t("pages:adminFdroidPage.filters")}</Typography>
                                        {filterActive ? (
                                            <Typography variant="caption" color="error">
                                                * {t("pages:adminFdroidPage.youHaveActiveFilters")}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                }
                            />
                            <CardContent className={classes.cardContent}>
                                <TextField name="workspaceId" label={t("common:label.workspace")} fullWidth type="number" />
                                <TextField name="name" label={t("common:label.name")} fullWidth />
                                <TextField name="title" label={t("common:label.title")} fullWidth />
                                <TextField name="url" label={t("common:label.url")} fullWidth />
                                <Box className={classes.filterActions}>
                                    <PrimaryButton type="submit" disabled={pristine}>
                                        {t("common:button.apply")}
                                    </PrimaryButton>
                                    <SecondaryButton disabled={pristine} onClick={() => onClear(form)}>
                                        {t("common:button.clear")}
                                    </SecondaryButton>
                                </Box>
                            </CardContent>
                        </Card>
                    </form>
                )}
            />

            <Box className={layoutClasses.tableLayout}>
                <Box className={layoutClasses.tableWrapper}>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                        <MinimalTableHeaderCell width={80} />
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody<API.FdroidRepoAdmin>
                                table={table}
                                loading={isLoading}
                                noDataMessage={t("pages:adminFdroidPage.noRepositoriesFound")}
                                filter={{ isActive: !!filterActive, onResetFilter: handleResetFilter }}
                                error={{ isError, onError: refetch }}
                            >
                                {table.getRowModel().rows.map((row) => (
                                    <TableItem key={row.original.id} row={row} onDeleteSuccess={refetch} />
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                </Box>

                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    rowsPerPageOptions={[12, 24, 48]}
                    count={data.count}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_event, page) => setPageIndex(page)}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : 12;
                        setPageSize(size);
                        setPageIndex(0);
                    }}
                />
            </Box>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminFdroidPage, {
        title: i18next.t("pages:adminFdroidPage.metaTitle"),
        description: i18next.t("pages:adminFdroidPage.metaDescription"),
    }),
    roles.accessRoles.AdminSupportAccessRoles,
);
